import axiosInstance from '../axiosInstance';
import type { AxiosResponse } from 'axios';
import type { WorkOrder, WorkOrderGas, WorkOrderGasReportRow, CreateWorkOrderRequest, CalendarFilterRequest, CalendarItemsResponse, WorkOrderLogEntry, TaskStep, StepType } from './types';
import { GeneralFormatter } from '@/composables/formatters';
import { dtoToTaskStep, resolveStepTypeId, type StepDtoBase } from './stepMapping';
import taskAPI from './taskAPI';
import type { SystemOfMeasurement } from '@/services/UserPreferencesService';

// Backend wire shape for a work-order step (stepTypeID FK; editor model is the shared TaskStep).
interface WorkOrderStepDto extends StepDtoBase {
  workOrderStepID?: number;
  sourceTaskStepID?: number | null;
  startedAt?: string | null;
  completedAt?: string | null;
  completedBy?: string | null;
}

const fromWorkOrderStepDto = (dto: WorkOrderStepDto): TaskStep => ({
  ...dtoToTaskStep(dto, dto.workOrderStepID),
  sourceTaskStepID: dto.sourceTaskStepID ?? undefined,
  startedAt: dto.startedAt ?? undefined,
  completedAt: dto.completedAt ?? undefined,
  completedBy: dto.completedBy ?? undefined,
});

const toWorkOrderStepDtos = (steps: TaskStep[], stepTypes: StepType[]): WorkOrderStepDto[] =>
  steps.map((step) => ({
    workOrderStepID: step.taskStepID,
    sequence: step.sequence,
    stepTypeID: resolveStepTypeId(step.type, stepTypes),
    description: step.description,
    isTracked: step.isTracked,
    assayCode: step.assayCode ?? null,
    assayRequired: step.assayRequired ?? false,
    sourceTaskStepID: step.sourceTaskStepID ?? null,
  }));

export interface WorkOrderGasReportFilters {
  sites?: string[] | null;
  workOrderNo?: number | null;
  eliCode?: string | null;
}

export interface WorkOrderFilterRequest {
  page: number;
  rows: number;
  startDate: Date;
  endDate: Date;
  selectedSites?: string[];
  selectedTrades?: string[];
  selectedCategories?: string[];
  selectedJobTypes?: string[];
  selectedPersonnelIDs?: number[];
  status?: string[];
  selectedDocTypes?: string[];
  noAttachments?: boolean;
  hasFsr?: boolean | null;
  selectedVendors?: string[];
  rescheduled?: boolean | null;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: number;
}

export interface WorkOrderExportRequest {
  selectedSites?: string[];
  selectedTrades?: string[];
  selectedCategories?: string[];
  selectedJobTypes?: string[];
  selectedPersonnelIDs?: number[];
  status?: string[];
  selectedDocTypes?: string[];
  noAttachments?: boolean;
  hasFsr?: boolean | null;
  selectedVendors?: string[];
  startDate?: string;
  endDate?: string;
  rescheduled?: boolean | null;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: number;
  selectedIds?: number[];
  excludedIds?: number[];
}

export interface WorkOrderBulkActionRequest extends WorkOrderExportRequest {
  action: string;
  assignedUserId?: number;
  completedOn?: string;
  completedByUserId?: number;
  rescheduleTo?: string;
  rescheduleReason?: string;
  rescheduleOtherReason?: string;
  acceptedByECX?: boolean;
  completedOnTime?: boolean | null;
  completionNotes?: string;
}

export interface WorkOrderBulkActionResult {
  affected: number;
  requested?: number;
  skipped?: number;
  // Complete only: rows excluded because a mandatory assay is uncaptured (subset of skipped).
  skippedForUnmetAssays?: number;
  // Reassign / Reschedule only: rows excluded because the work order is associated with a MOP (subset of skipped).
  skippedForMopLock?: number;
}

export interface WorkOrderGroupUpdateResult {
  data: WorkOrder[];
  totalRecords: number;
  // Rows the API refused to change (e.g. associated with a MOP), each with the reason.
  skipped?: { workOrderID: number; reason: string }[];
}

type WorkOrderWire = Omit<WorkOrder, 'dueDate' | 'completedOn' | 'deletedOn'> & {
  dueDate?: string | Date | null;
  completedOn?: string | Date | null;
  deletedOn?: string | Date | null;
};

const toDate = (value?: string | Date | null): Date | undefined =>
  value ? new Date(value) : undefined;

const convertToWorkOrders = (data: WorkOrderWire[]): WorkOrder[] => {
  return data.map((wo) => ({
    ...wo,
    dueDate: toDate(wo.dueDate),
    completedOn: toDate(wo.completedOn),
    deletedOn: toDate(wo.deletedOn)
  }));
}

export interface LookupItem {
  code: string;
  name: string;
}

export interface LookupDataResult {
  trades: LookupItem[];
  jobTypes: string[];
  criticalities: string[];
}

export interface CategoryTradeMapping {
  code: string;
  tradeClass: string;
}

export interface FilterOptions {
  trades: string[];
  categories: string[];
  vendors: string[];
  categoryTradeMap: CategoryTradeMapping[];
}

export default {
  async getLookupData(): Promise<LookupDataResult> {
    try {
      const response = await axiosInstance.get('/api/WorkOrder/lookups');
      return response.data;
    } catch (error) {
      console.error('Error fetching lookup data:', error);
      throw error;
    }
  },
  async getFilterOptions(sites: string[]): Promise<FilterOptions> {
    try {
      const response = await axiosInstance.post('/api/WorkOrder/filterOptions', { selectedSites: sites });
      return response.data;
    } catch (error) {
      console.error('Error fetching filter options:', error);
      throw error;
    }
  },
  async getWorkOrdersByFilters(filter: WorkOrderFilterRequest): Promise<{ data: WorkOrder[], totalRecords: number }> {
    try {
      const body = {
        page: filter.page,
        rows: filter.rows,
        selectedSites: filter.selectedSites ?? [],
        selectedTrades: filter.selectedTrades ?? [],
        selectedCategories: filter.selectedCategories ?? [],
        selectedJobTypes: filter.selectedJobTypes ?? [],
        selectedPersonnelIDs: filter.selectedPersonnelIDs ?? [],
        status: filter.status ?? [],
        selectedDocTypes: filter.selectedDocTypes ?? [],
        noAttachments: filter.noAttachments ?? false,
        hasFsr: filter.hasFsr ?? null,
        selectedVendors: filter.selectedVendors ?? [],
        // #4217 — must carry the user's local UTC offset (not toISOString → UTC),
        // otherwise the server's inclusive end-of-day pin lands on the wrong calendar day.
        startDate: GeneralFormatter.toLocalISOString(new Date(filter.startDate)),
        endDate: GeneralFormatter.toLocalISOString(new Date(filter.endDate)),
        rescheduled: filter.rescheduled ?? null,
        searchQuery: filter.searchQuery ?? '',
        sortField: filter.sortField ?? '',
        sortOrder: filter.sortOrder ?? 1
      };

      console.log('Getting WOs with body:', body);

      const response = await axiosInstance.post('/api/WorkOrder/filtered', body);

      console.log('Response:', response);

      return {
        data: convertToWorkOrders(response.data.data),
        totalRecords: response.data.totalRecords
      };

    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getWorkOrderById(id: number): Promise<WorkOrder> {
    try {
      const response = await axiosInstance.get(`/api/WorkOrder/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching work order by ID:', error);
      throw error;
    }
  },
  async getChildWorkOrders(id: number): Promise<WorkOrder[]> {
    try {
      const response = await axiosInstance.get(`/api/WorkOrder/${id}/children`);
      return convertToWorkOrders(response.data);
    } catch (error) {
      console.error('Error fetching child work orders:', error);
      throw error;
    }
  },
  async getWorkOrdersForAsset(eliCode: string, page: number, rows: number, sortField: string = 'workOrderID', sortOrder: number): Promise<{ data: WorkOrder[], totalRecords: number }> {
    try {
      console.log("Getting Work Orders with:", eliCode, page, rows, sortField, sortOrder);
      const response = await axiosInstance.get(`/api/WorkOrder/asset`, {
        params: {
          eliCode,
          page,
          rows,
          sortField,
          sortOrder
        }
      });
      console.log('Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getWorkOrdersForEquipment(equipmentID: number, page: number, rows: number, sortField: string = 'workOrderID', sortOrder: number): Promise<{ data: WorkOrder[], totalRecords: number }> {
    try {
      const response = await axiosInstance.get(`/api/WorkOrder/equipment`, {
        params: {
          equipmentID,
          page,
          rows,
          sortField,
          sortOrder
        }
      });
      console.log('Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getCalendarData(request: CalendarFilterRequest, signal?: AbortSignal): Promise<CalendarItemsResponse> {
    const response = await axiosInstance.post<CalendarItemsResponse>(`/api/WorkOrder/calendar`, request, {
      timeout: 0,
      signal,
    });
    return {
      workOrders: response.data.workOrders ?? [],
      scheduledWorkOrders: response.data.scheduledWorkOrders ?? [],
      totalRecords: response.data.totalRecords ?? 0,
    };
  },
  async createWorkOrder(request: CreateWorkOrderRequest): Promise<{ workOrderIds: number[] }> {
    try {
      const { steps, ...payload } = request;
      const body = steps?.length
        ? { ...payload, steps: toWorkOrderStepDtos(steps, await taskAPI.getStepTypes()) }
        : payload;
      const response = await axiosInstance.post(`/api/WorkOrder/create`, body);
      return response.data;
    } catch (error) {
      console.error('Error creating work order:', error);
      throw error;
    }
  },

  async getWorkOrderSteps(workOrderId: number): Promise<TaskStep[]> {
    try {
      const response = await axiosInstance.get(`/api/WorkOrder/${workOrderId}/Steps`);
      return (response.data as WorkOrderStepDto[]).map(fromWorkOrderStepDto);
    } catch (error) {
      console.error('Error fetching work order steps:', error);
      throw error;
    }
  },

  async replaceWorkOrderSteps(workOrderId: number, steps: TaskStep[]): Promise<TaskStep[]> {
    try {
      const payload = toWorkOrderStepDtos(steps, await taskAPI.getStepTypes());
      const response = await axiosInstance.put(`/api/WorkOrder/${workOrderId}/Steps`, payload);
      return (response.data as WorkOrderStepDto[]).map(fromWorkOrderStepDto);
    } catch (error) {
      console.error('Error saving work order steps:', error);
      throw error;
    }
  },

  async updateWorkOrder(workOrder: WorkOrder): Promise<AxiosResponse<WorkOrder>> {
    try {
      const response = await axiosInstance.post(`/api/WorkOrder/update`, workOrder);
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  async groupUpdateWorkOrder(workOrders: WorkOrder[]): Promise<AxiosResponse<WorkOrderGroupUpdateResult>> {
    try {
      const response = await axiosInstance.post(`/api/WorkOrder/group-update`, workOrders);
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },

  async completeWorkOrder(workOrder: WorkOrder): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.put(`/api/WorkOrder/complete`, workOrder);
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async closeWorkOrder(workOrder: WorkOrder): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.put(`/api/WorkOrder/close`, workOrder);
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async reopenWorkOrder(workOrder: WorkOrder): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.put(`/api/WorkOrder/reopen`, workOrder);
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async reassignWorkOrder(workOrder: WorkOrder, selectedUser: string): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.put(`/api/WorkOrder/reassign`, workOrder, {
        params: {
          selectedUser
        }
      });
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async resetWorkOrder(workOrder: WorkOrder): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.put(`/api/WorkOrder/reset`, workOrder);
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async cancelWorkOrder(workOrder: WorkOrder): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.put(`/api/WorkOrder/cancel`, workOrder);
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async rescheduleWorkOrder(workOrder: WorkOrder): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.put(`/api/WorkOrder/reschedule`, workOrder);
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getWorkOrderGases(workOrderID: number, sortField: string = '', sortOrder: number = 1): Promise<{ data: WorkOrderGas[] }> {
    try {
      const response = await axiosInstance.get(`/api/WorkOrderGas/getGasesByWO`, {
        params: {
          workOrderID,
          sortField,
          sortOrder
        }
      });
      console.log('Response: ', response);
      return response.data;
    } catch (error) {
      console.error('Error: ', error);
      throw error;
    }
  },
  async saveGasInfo(formData: Record<string, string>): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.post(`/api/WorkOrderGas/saveGases`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deleteWorkOrderGas(workOrderGas: number): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.put(`/api/WorkOrderGas/delete`, workOrderGas);
      console.log('Response: ', response.data);
      return response;
    } catch (error) {
      console.error('Error: ', error);
      throw error;
    }
  },
  async attachDocument(formData: FormData, signal?: AbortSignal, onUploadProgress?: (pct: number) => void): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.post(`/api/WorkOrder/attachDocument`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        signal,
        timeout: 0,
        onUploadProgress: onUploadProgress
          ? (e) => { if (e.total) onUploadProgress(Math.round((e.loaded / e.total) * 100)); }
          : undefined,
      });
      console.log('Response: ', response.data);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async deleteWorkOrders(workOrders: WorkOrder[]): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.put(`/api/WorkOrder/group-delete`, workOrders);
      console.log('Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error: ', error);
      throw error;
    }
  },
  async exportWorkOrders(request: WorkOrderExportRequest): Promise<Blob> {
    const response = await axiosInstance.post('/api/WorkOrder/export', request, {
      responseType: 'blob',
      timeout: 0,
    });
    return response.data;
  },
  async bulkAction(request: WorkOrderBulkActionRequest): Promise<AxiosResponse<WorkOrderBulkActionResult>> {
    const response = await axiosInstance.post('/api/WorkOrder/bulk-action', request);
    return response;
  },
  async getWorkOrderLog(workOrderId: number, dateFrom?: Date): Promise<WorkOrderLogEntry[]> {
    try {
      const params: Record<string, string> = {};
      if (dateFrom) {
        const year = dateFrom.getFullYear();
        const month = String(dateFrom.getMonth() + 1).padStart(2, '0');
        const day = String(dateFrom.getDate()).padStart(2, '0');
        params.dateFrom = `${year}-${month}-${day}`;
      }
      const response = await axiosInstance.get(`/api/WorkOrder/${workOrderId}/log`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching work order log:', error);
      throw error;
    }
  },
  async getWorkOrderGasReport(page: number, rows: number, filters: WorkOrderGasReportFilters,
    sortField: string = '', sortOrder: number = 1,
    systemOfMeasurement: SystemOfMeasurement | null = null): Promise<{ data: WorkOrderGasReportRow[], totalRecords: number }> {
    try {
      const body = {
        page,
        rows,
        sites: filters.sites?.length ? filters.sites : null,
        workOrderNo: filters.workOrderNo ?? null,
        eliCode: filters.eliCode ?? null,
        sortField,
        sortOrder,
        systemOfMeasurement,
      };
      const response = await axiosInstance.post('/api/WorkOrderGas/report', body);
      return response.data;
    } catch (error) {
      console.error('Error fetching work order gas report:', error);
      throw error;
    }
  },
  async exportWorkOrderGasReport(filters: WorkOrderGasReportFilters,
    sortField: string | null = null, sortOrder: number = 1,
    systemOfMeasurement: SystemOfMeasurement | null = null): Promise<Blob> {
    const body = {
      sites: filters.sites?.length ? filters.sites : null,
      workOrderNo: filters.workOrderNo ?? null,
      eliCode: filters.eliCode ?? null,
      sortField,
      sortOrder,
      systemOfMeasurement,
    };
    const response = await axiosInstance.post('/api/WorkOrderGas/report/export', body, {
      responseType: 'blob',
      timeout: 0,
    });
    return response.data;
  },
};
